import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Droplet, Eye, EyeOff } from 'lucide-react';

import { KnowledgeParticleQaSections } from '../../components/KnowledgeParticleQaSections';
import { MathTextBlock } from '../../components/MathTextBlock';
import type { KnowledgeParticle } from '../../types/domain';
import type { ReviewFeedbackKind } from '../../services/reviewService';
import type { RevisionFamilyDeckCard } from './reviewDeckBuilder';
import { FAMILY_SUMMARY_SCORE_OPTIONS } from './reviewDeckBuilder';
import { MemoryScoreTrack } from './MemoryScoreTrack';
import { parseInterpretingStimulus, isInterpretingParticle } from '../../utils/interpretingScript';

type RevisionFamilyDeckWebProps = {
  cards: RevisionFamilyDeckCard[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  particlesById: Record<string, KnowledgeParticle>;
  leadThoughtByParticleId?: Record<string, { text?: string }>;
  familyFeedbackByParticleId?: Record<string, { score?: number | null; feedback?: string | null }>;
  onLeadThoughtChange?: (particleId: string, text: string) => void;
  onSubmitLeadThought?: (card: RevisionFamilyDeckCard) => Promise<void>;
  onFamilySummaryChange?: (particleId: string, text: string) => void;
  onFamilySummaryScore?: (particleId: string, score: number) => void;
  onSubmitFamilySummary?: (card: RevisionFamilyDeckCard) => Promise<void>;
  onReviewFeedback?: (card: RevisionFamilyDeckCard, feedback: ReviewFeedbackKind) => Promise<void>;
};

function InterpretingStimulusWeb({ text }: { text: string }) {
  const parsed = parseInterpretingStimulus(text);
  const [showChinese, setShowChinese] = useState(false);
  const canSwitch = Boolean(parsed?.chinese);
  const body = parsed
    ? (showChinese && canSwitch ? parsed.chinese : parsed.basicEnglish)
    : text;
  return (
    <div className="space-y-3">
      {parsed?.title ? <div className="font-semibold text-ink">{parsed.title}</div> : null}
      <button
        type="button"
        onClick={() => {
          if (canSwitch) setShowChinese((current) => !current);
        }}
        disabled={!canSwitch}
        className="block w-full text-left"
      >
        {parsed ? (
          <div className="text-xs font-semibold text-sky-700">
            {showChinese && canSwitch ? '中文' : 'Basic English'}
            {canSwitch ? ' · 点击切换' : ''}
          </div>
        ) : null}
        <div className={parsed ? 'mt-2' : undefined}>
          <MathTextBlock text={body} variant="document" />
        </div>
      </button>
      <div className="min-h-[10rem] text-xs text-muted">在此写下单词、短语或一句英文</div>
    </div>
  );
}

export function RevisionFamilyDeckWeb({
  cards,
  currentIndex,
  onIndexChange,
  particlesById,
  leadThoughtByParticleId = {},
  familyFeedbackByParticleId = {},
  onLeadThoughtChange,
  onSubmitLeadThought,
  onFamilySummaryChange,
  onFamilySummaryScore,
  onSubmitFamilySummary,
  onReviewFeedback,
}: RevisionFamilyDeckWebProps) {
  const [revealed, setRevealed] = useState(false);
  const [selectedFeedbackByCardId, setSelectedFeedbackByCardId] = useState<Record<string, ReviewFeedbackKind>>({});
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const card = cards[currentIndex];
  const particle = card ? particlesById[card.particleId] : undefined;

  const progressLabel = useMemo(() => {
    if (!card) return '';
    return `卡 ${currentIndex + 1} / ${cards.length} · 套 ${(card.familyIndex ?? 0) + 1}`;
  }, [card, cards.length, currentIndex]);

  if (!card) {
    return <p className="text-muted">暂无复习卡片。</p>;
  }

  const isOpening = card.streamDecorationKind === 'opening';
  const isFamilySummary = card.cardKind === 'family-summary';
  const isMemoryProtocol = card.familyFeedbackProtocol === 'memory_per_card';
  const isMemoryOverview = isMemoryProtocol && Boolean(card.isFamilyFinalCard);
  const leadThought = leadThoughtByParticleId[card.particleId]?.text || '';
  const familyFeedback = familyFeedbackByParticleId[card.particleId];
  const stemText = String(
    card.sharedProblemContent
      || card.sharedProblemTitle
      || (!isMemoryOverview ? card.questionMain : '')
      || '',
  ).trim();

  function goNext() {
    setRevealed(false);
    onIndexChange(Math.min(currentIndex + 1, cards.length - 1));
  }

  function goPrev() {
    setRevealed(false);
    onIndexChange(Math.max(currentIndex - 1, 0));
  }

  function selectMemoryRating(kind: ReviewFeedbackKind) {
    if (!card || feedbackSaving) return;
    setSelectedFeedbackByCardId((current) => ({ ...current, [card.id]: kind }));
    const score = kind === 'proficient' ? 92 : kind === 'fuzzy' ? 70 : 35;
    onFamilySummaryScore?.(card.particleId, score);
  }

  async function handleContinueMemoryPractice() {
    if (!card || feedbackSaving) return;
    const kind = selectedFeedbackByCardId[card.id];
    if (!kind) return;
    setFeedbackSaving(true);
    try {
      await onReviewFeedback?.(card, kind);
      setRevealed(false);
    } finally {
      setFeedbackSaving(false);
    }
  }

  const showMemoryTriad = isMemoryProtocol
    ? Boolean(card.isFamilyFinalCard) && revealed
    : card.cardKind === 'sub-question';
  const selectedFeedback = selectedFeedbackByCardId[card.id] ?? null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-muted">
          {progressLabel}
          {card.subjectLabel ? <span className="ml-2">· {card.subjectLabel}</span> : null}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={goPrev} disabled={currentIndex <= 0} className="rounded-lg border border-border p-2 disabled:opacity-40">
            <ChevronLeft size={18} />
          </button>
          <button type="button" onClick={goNext} disabled={currentIndex >= cards.length - 1 || (isMemoryOverview && !selectedFeedback)} className="rounded-lg border border-border p-2 disabled:opacity-40">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {stemText && (isMemoryProtocol || card.cardKind === 'sub-question' || card.cardKind === 'full-answer') ? (
        <div className="rounded-2xl border border-primary/15 bg-[#EAF2FF]/55 px-5 py-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-primary-strong/80">题目</div>
          <div className="mt-2 text-[1.125rem] font-semibold leading-relaxed text-ink">
            <MathTextBlock text={stemText} variant="document" />
          </div>
        </div>
      ) : null}

      <article className="rounded-2xl border border-border bg-surface p-5 shadow-card">
        {!isMemoryOverview ? (
          <div className="mb-3 flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-[#EAF2FF] px-2.5 py-1 font-medium text-primary-strong">{card.cardKind || 'card'}</span>
            {card.factoryDrip ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2.5 py-1 font-medium text-teal-800">
                <Droplet className="h-3.5 w-3.5" />
                滴灌
              </span>
            ) : null}
            {isOpening ? <span className="rounded-full bg-amber-50 px-2.5 py-1 text-amber-800">起手卡</span> : null}
            <span className="rounded-full bg-canvas px-2.5 py-1 text-muted">{card.subjectLabel}</span>
          </div>
        ) : null}

        {!isMemoryOverview && card.title ? (
          <h2 className="text-lg font-semibold text-ink">{card.title}</h2>
        ) : null}

        {isOpening ? (
          <div className="mt-4 space-y-3">
            <MathTextBlock text={card.questionMain} variant="document" />
            <label className="block text-sm">
              <span className="font-medium text-ink">起始思路</span>
              <textarea
                value={leadThought}
                onChange={(event) => onLeadThoughtChange?.(card.particleId, event.target.value)}
                rows={4}
                placeholder="说说你的起始思路、考查内容、基本思想方法，或者直接说完全没思路"
                className="mt-2 w-full rounded-lg border border-border px-3 py-2"
              />
            </label>
            <button
              type="button"
              onClick={() => void onSubmitLeadThought?.(card)}
              className="rounded-lg bg-primary px-4 py-2 text-sm text-white"
            >
              保存起手并继续
            </button>
          </div>
        ) : null}

        {!isOpening && !isFamilySummary && isMemoryOverview ? (
          <>
            {!revealed ? (
              <div className="mt-2 flex min-h-[9rem] flex-col items-center justify-center rounded-xl border border-dashed border-border bg-canvas/70 px-4 py-8 text-center">
                <p className="text-sm font-medium text-muted">先回想答案，再点下方显示</p>
              </div>
            ) : (
              <div className="mt-2 rounded-xl border border-primary/15 bg-[#EAF2FF]/40 p-4">
                <MathTextBlock text={joinAnswer(card) || '暂无答案'} variant="document" />
              </div>
            )}

            <button
              type="button"
              onClick={() => setRevealed((value) => !value)}
              className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary"
            >
              {revealed ? <EyeOff size={16} /> : <Eye size={16} />}
              {revealed ? '隐藏答案' : '显示答案'}
            </button>

            {showMemoryTriad ? (
              <div className="mt-4 space-y-4">
                <MemoryScoreTrack
                  currentRating={selectedFeedback}
                  disabled={feedbackSaving}
                  onSelect={selectMemoryRating}
                />
                {selectedFeedback ? (
                  <div className="space-y-3">
                    <label className="block text-sm">
                      <span className="font-medium text-ink">练习评注</span>
                      <p className="mt-1 text-xs text-muted">
                        记下此刻的判断依据：哪里想起来了、哪里卡住、下次怎么练。这是刻意练习的评价能力训练。
                      </p>
                      <textarea
                        value={familyFeedback?.feedback || ''}
                        onChange={(event) => onFamilySummaryChange?.(card.particleId, event.target.value)}
                        rows={3}
                        placeholder="例如：公式能想起，但条件转化那一步断了"
                        className="mt-2 w-full rounded-lg border border-border px-3 py-2 text-sm"
                        disabled={feedbackSaving}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => { void handleContinueMemoryPractice(); }}
                      disabled={feedbackSaving}
                      className="rounded-lg bg-primary px-4 py-2 text-sm text-white disabled:opacity-50"
                    >
                      {feedbackSaving ? '保存中…' : '保存并进入下一题'}
                    </button>
                    <p className="text-xs text-muted">评注可选；确认后才会进入下一题。</p>
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        ) : null}

        {!isOpening && !isFamilySummary && !isMemoryOverview ? (
          <>
            <div className="mt-4 text-[1.05rem] font-medium leading-relaxed text-ink">
              {isInterpretingParticle(card.questionMain, card.answerMain, card.sharedProblemContent) ? (
                <InterpretingStimulusWeb text={card.questionMain} />
              ) : (
                <>
                  <MathTextBlock text={card.questionMain} variant="document" />
                  {card.questionItems.map((item, index) => (
                    <div key={index} className="mt-2">
                      <MathTextBlock text={item} variant="document" />
                    </div>
                  ))}
                </>
              )}
            </div>

            <button
              type="button"
              onClick={() => setRevealed((value) => !value)}
              className="mt-4 inline-flex items-center gap-2 text-sm text-primary"
            >
              {revealed ? <EyeOff size={16} /> : <Eye size={16} />}
              {revealed ? '隐藏答案' : '显示答案'}
            </button>

            {revealed ? (
              <div className="mt-4 rounded-xl border border-primary/15 bg-[#EAF2FF]/40 p-4">
                {card.cardKind === 'full-answer' && card.fullAnswerSections ? (
                  <div className="space-y-4">
                    {card.fullAnswerSections.map((section) => (
                      <div key={section.id}>
                        <div className="text-sm font-medium text-ink">{section.label}</div>
                        {section.content ? <MathTextBlock text={section.content} variant="document" className="mt-1" /> : null}
                        {section.answer ? (
                          <div className="mt-2 text-sm text-muted">
                            <MathTextBlock text={section.answer} variant="document" />
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <MathTextBlock text={joinAnswer(card)} variant="document" />
                )}
              </div>
            ) : null}

            {showMemoryTriad ? (
              <div className="mt-4 space-y-4">
                <MemoryScoreTrack
                  currentRating={selectedFeedback}
                  disabled={feedbackSaving}
                  onSelect={selectMemoryRating}
                />
                {selectedFeedback ? (
                  <div className="space-y-3">
                    <label className="block text-sm">
                      <span className="font-medium text-ink">练习评注</span>
                      <textarea
                        value={familyFeedback?.feedback || ''}
                        onChange={(event) => onFamilySummaryChange?.(card.particleId, event.target.value)}
                        rows={3}
                        placeholder="例如：公式能想起，但条件转化那一步断了"
                        className="mt-2 w-full rounded-lg border border-border px-3 py-2 text-sm"
                        disabled={feedbackSaving}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => { void handleContinueMemoryPractice(); }}
                      disabled={feedbackSaving}
                      className="rounded-lg bg-primary px-4 py-2 text-sm text-white disabled:opacity-50"
                    >
                      {feedbackSaving ? '保存中…' : '保存并进入下一题'}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        ) : null}

        {isFamilySummary && card.familyFeedbackProtocol !== 'memory_per_card' ? (
          <div className="mt-4 space-y-4">
            <p className="text-sm text-muted">{card.questionMain}</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {FAMILY_SUMMARY_SCORE_OPTIONS.map((option) => (
                <button
                  key={option.score}
                  type="button"
                  onClick={() => onFamilySummaryScore?.(card.particleId, option.score)}
                  className={`rounded-xl border px-3 py-3 text-left text-sm ${
                    familyFeedback?.score === option.score ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                >
                  <div className="font-medium text-ink">{option.label}</div>
                  <div className="mt-1 text-xs text-muted">{option.description}</div>
                </button>
              ))}
            </div>
            <textarea
              value={familyFeedback?.feedback || ''}
              onChange={(event) => onFamilySummaryChange?.(card.particleId, event.target.value)}
              rows={4}
              placeholder="哪里顺、哪里卡、还差哪一步就稳定了"
              className="w-full rounded-lg border border-border px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={() => void onSubmitFamilySummary?.(card)}
              className="rounded-lg bg-primary px-4 py-2 text-sm text-white"
            >
              保存总结并进入下一套
            </button>
          </div>
        ) : null}

        {particle && card.cardKind === 'home-question' ? (
          <div className="mt-6 border-t border-border pt-4">
            <KnowledgeParticleQaSections particle={particle} fallbackQuestionText={particle.title} />
          </div>
        ) : null}
      </article>
    </div>
  );
}

function joinAnswer(card: RevisionFamilyDeckCard) {
  return [card.answerMain, ...card.answerItems].filter(Boolean).join('\n\n');
}
